import { sfxUiTick } from '@/audio/sfx';
import { hapticTick } from '@/ui/haptics';

/**
 * backNav.ts — M114b: the ONE back affordance (owner ask: uniform icon-only
 * back buttons in the upper-left, plus an iOS-style swipe-from-the-left).
 *
 * Two halves:
 *
 *  1. A tiny REGISTRY. `backButton()` (ui/dom.ts) registers every shared back
 *     button here as it is CREATED, and the swipe resolves the ACTIVE one by
 *     VISIBILITY. So a screen that has a back button gets the gesture for
 *     free, and a screen that deliberately has none — Pause, Results, the
 *     onboarding flow, the Racer-ID boot gate in `required` mode — can never
 *     be swiped out of. The DOM is the source of truth; nothing to keep in
 *     sync. Clearing is TOKEN-GUARDED: `clearBackAction(token)` removes that
 *     one entry, so a late/out-of-order teardown can never wipe a newer
 *     screen's action.
 *
 *  2. The GESTURE. A drag that STARTS within EDGE_START_PX of the left edge,
 *     proves itself horizontal, and passes the commit threshold (distance OR
 *     a fast flick) runs that action. Everything else springs back. The live
 *     drag translates the screen with transform/opacity only — never layout —
 *     and every exit path clears the inline styles, so an interrupted gesture
 *     can never leave a stuck transform.
 *
 * THE RACE RULE (owner, non-negotiable): a player must NEVER be swiped out of
 * a live race. TWO independent layers enforce it —
 *   (a) `setSwipeBackEnabled` defaults to DENY: Flow.hideAll() turns the
 *       gesture OFF and only a menu screen (Flow.attractMenu) turns it back
 *       on, so racing / paused / the M112 tutorial demo are all off; and
 *   (b) a live race has NO visible registered back button, so `pickActive`
 *       resolves nothing and there is nothing to fire even if (a) were wrong.
 */

// ---------------------------------------------------------------------------
// Pure decision core (unit-tested — tests/ui/backNav.test.ts)
// ---------------------------------------------------------------------------

/** How close to the left edge a touch must START (iOS uses a ~20-30px strip). */
export const EDGE_START_PX = 26;
/** Movement below this is noise — the gesture stays undecided. */
export const SLOP_PX = 10;
/** Horizontal must beat vertical by this much before we take the gesture. */
export const HORIZONTAL_RATIO = 1.5;
/** Past this fraction of the viewport width the swipe completes on release. */
export const COMMIT_FRACTION = 0.32;
/** …or a flick at least this fast (px per ms) completes it early. */
export const FLICK_VELOCITY_PX_MS = 0.5;
/** A flick still needs SOME travel, so a tap-with-a-twitch never navigates. */
export const FLICK_MIN_PX = 30;
/** The drag never pushes the screen further than this fraction across. */
export const MAX_DRAG_FRACTION = 0.55;
/** Finger-to-screen ratio at the start of the drag (the rest is rubber-band). */
export const DRAG_DAMPING = 0.62;

export type GesturePhase = 'pending' | 'tracking' | 'reject';

/** Did this touch start in the left edge strip? */
export function isEdgeStart(clientX: number, edge: number = EDGE_START_PX): boolean {
  return clientX >= 0 && clientX <= edge;
}

/**
 * Decide what a drag is, given its total delta from the start point.
 * `pending` = still undecided (keep watching), `tracking` = it's a back
 * swipe, `reject` = hands off (a scroll, or a leftward drag).
 */
export function classifyDrag(dx: number, dy: number): GesturePhase {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  // Vertical intent wins outright — let the page scroll.
  if (ay > SLOP_PX && ay >= ax) return 'reject';
  // A clear LEFTWARD drag is never a back swipe.
  if (dx < -SLOP_PX) return 'reject';
  // A clear rightward drag past the slop takes the gesture.
  if (dx > SLOP_PX && ax > ay * HORIZONTAL_RATIO) return 'tracking';
  return 'pending';
}

/** Release decision: far enough across, or fast enough on the way out. */
export function shouldCommit(dx: number, viewportWidth: number, velocityPxPerMs: number): boolean {
  if (dx <= 0 || viewportWidth <= 0) return false;
  if (dx >= viewportWidth * COMMIT_FRACTION) return true;
  return velocityPxPerMs >= FLICK_VELOCITY_PX_MS && dx >= FLICK_MIN_PX;
}

/**
 * How far the screen actually moves for a finger delta of `dx`. Starts at
 * ~DRAG_DAMPING of the finger and eases asymptotically toward the ceiling, so
 * the sheet always feels attached and never runs off the screen mid-drag.
 */
export function dragOffset(dx: number, viewportWidth: number): number {
  if (dx <= 0 || viewportWidth <= 0) return 0;
  const max = viewportWidth * MAX_DRAG_FRACTION;
  return max * (1 - Math.exp(-(dx * DRAG_DAMPING) / max));
}

/** The newest VISIBLE registration wins (screens are mutually exclusive, so
 *  in practice there is exactly one — this just makes ties deterministic). */
export function pickActive<T extends { visible: boolean }>(entries: readonly T[]): T | null {
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i].visible) return entries[i];
  }
  return null;
}

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

interface BackEntry {
  token: number;
  el: HTMLElement;
  action: () => void;
}

let entries: BackEntry[] = [];
let nextToken = 1;
let enabled = false; // DEFAULT DENY — only a menu screen ever turns it on
let modalOpen = false; // M115: a confirm dialog is up — the screen behind is frozen
let reducedMotion = false;

/** Register a back affordance. Returns the token used to clear it. */
export function registerBackAction(el: HTMLElement, action: () => void): number {
  const token = nextToken++;
  entries.push({ token, el, action });
  return token;
}

/** Token-guarded removal: only ever drops THIS registration. */
export function clearBackAction(token: number): void {
  entries = entries.filter((e) => e.token !== token);
}

/** The hard gate (layer a). Flow denies by default and allows on menus. */
export function setSwipeBackEnabled(on: boolean): void {
  enabled = on;
  if (!on) cancelSwipeBack();
}

export function isSwipeBackEnabled(): boolean {
  return enabled;
}

/**
 * M115 — a modal ("Are you sure?") is on screen, so the screen UNDER it must not
 * swipe away from beneath the question. Set true/false by ui/confirm.ts, whose
 * every teardown path runs through closeConfirm() — so this can never latch on.
 */
export function setBackNavModal(on: boolean): void {
  modalOpen = on;
  if (on) cancelSwipeBack();
}

/** The game's own reduced-motion setting (the CSS media query is honoured too). */
export function setBackNavReducedMotion(on: boolean): void {
  reducedMotion = on;
}

/** Test seam. */
export function resetBackNav(): void {
  cancelSwipeBack();
  entries = [];
  nextToken = 1;
  enabled = false;
  modalOpen = false;
  reducedMotion = false;
}

/** Test seam / diagnostics. */
export function backActionCount(): number {
  return entries.length;
}

// ---------------------------------------------------------------------------
// The gesture
// ---------------------------------------------------------------------------

const OUT_MS = 190; // the slide-out beat before the action fires
const SPRING_MS = 210;

interface Gesture {
  id: number;
  x0: number;
  y0: number;
  phase: GesturePhase;
  entry: BackEntry;
  screen: HTMLElement;
  lastX: number;
  lastT: number;
  prevX: number;
  prevT: number;
}

let gesture: Gesture | null = null;
let settleTimer: number | null = null;

function prefersReducedMotion(): boolean {
  if (reducedMotion) return true;
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

/** jQuery's `:visible` idiom — false for anything in a display:none subtree. */
function isElementVisible(el: HTMLElement): boolean {
  return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

/** Never steal a swipe from a text field, a slider, or a horizontal scroller. */
function blocksSwipe(target: EventTarget | null, screen: HTMLElement): boolean {
  let node = target instanceof Element ? target : null;
  while (node && node !== screen.parentElement) {
    const tag = node.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (node instanceof HTMLElement && node.scrollWidth > node.clientWidth + 2) {
      const ox = getComputedStyle(node).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
    node = node.parentElement;
  }
  return false;
}

function isTyping(): boolean {
  const a = document.activeElement;
  if (!a) return false;
  const tag = a.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || (a as HTMLElement).isContentEditable === true;
}

function clearSettle(): void {
  if (settleTimer !== null) {
    window.clearTimeout(settleTimer);
    settleTimer = null;
  }
}

/** The ONE cleanup path — every exit routes through it, so no gesture can
 *  ever leave a screen stuck mid-transform. */
function resetScreen(screen: HTMLElement): void {
  screen.classList.remove('swipe-back-dragging', 'swipe-back-anim', 'swipe-back-out');
  screen.style.transform = '';
  screen.style.opacity = '';
  screen.style.willChange = '';
}

function paint(screen: HTMLElement, x: number, width: number): void {
  screen.style.transform = x > 0 ? `translate3d(${x.toFixed(1)}px,0,0)` : '';
  // A whisper of fade sells the "this page is leaving" read without a scrim
  // element (transform + opacity only — both GPU-composited).
  const t = width > 0 ? Math.min(1, x / (width * MAX_DRAG_FRACTION)) : 0;
  screen.style.opacity = x > 0 ? String(1 - 0.18 * t) : '';
}

function endGesture(): void {
  if (!gesture) return;
  const g = gesture;
  gesture = null;
  detachMoveListeners();
  resetScreen(g.screen);
}

function springBack(): void {
  if (!gesture) return;
  const g = gesture;
  gesture = null;
  detachMoveListeners();
  if (prefersReducedMotion()) {
    resetScreen(g.screen);
    return;
  }
  g.screen.classList.remove('swipe-back-dragging');
  g.screen.classList.add('swipe-back-anim');
  g.screen.style.transform = '';
  g.screen.style.opacity = '';
  clearSettle();
  settleTimer = window.setTimeout(() => {
    settleTimer = null;
    resetScreen(g.screen);
  }, SPRING_MS);
}

function commit(width: number): void {
  if (!gesture) return;
  const g = gesture;
  gesture = null;
  detachMoveListeners();
  // The tap-equivalent feedback pair — the swipe IS pressing that button.
  sfxUiTick();
  hapticTick();

  const fire = (): void => {
    resetScreen(g.screen);
    g.entry.action();
  };

  if (prefersReducedMotion()) {
    fire();
    return;
  }
  g.screen.classList.remove('swipe-back-dragging');
  g.screen.classList.add('swipe-back-anim', 'swipe-back-out');
  g.screen.style.transform = `translate3d(${Math.round(width)}px,0,0)`;
  g.screen.style.opacity = '0';
  clearSettle();
  settleTimer = window.setTimeout(() => {
    settleTimer = null;
    fire();
  }, OUT_MS);
}

/** Abandon anything in flight and clean up (Flow.hideAll, gate flips). */
export function cancelSwipeBack(): void {
  clearSettle();
  endGesture();
}

function findTouch(e: TouchEvent, id: number): Touch | null {
  for (let i = 0; i < e.changedTouches.length; i++) {
    const t = e.changedTouches[i];
    if (t.identifier === id) return t;
  }
  return null;
}

function onTouchMove(e: TouchEvent): void {
  const g = gesture;
  if (!g) return;
  if (e.touches.length > 1) {
    endGesture();
    return;
  }
  const t = findTouch(e, g.id);
  if (!t) return;
  const dx = t.clientX - g.x0;
  const dy = t.clientY - g.y0;

  if (g.phase === 'pending') {
    g.phase = classifyDrag(dx, dy);
    if (g.phase === 'reject') {
      endGesture();
      return;
    }
    if (g.phase === 'tracking') {
      g.screen.classList.add('swipe-back-dragging');
      g.screen.style.willChange = 'transform, opacity';
    }
  }
  if (g.phase !== 'tracking') return;

  // Own the gesture: stops the page scrolling AND stops the browser
  // synthesizing a click on whatever the finger started over.
  if (e.cancelable) e.preventDefault();

  const now = performance.now();
  if (now - g.lastT > 24) {
    g.prevX = g.lastX;
    g.prevT = g.lastT;
    g.lastX = t.clientX;
    g.lastT = now;
  } else {
    g.lastX = t.clientX;
  }
  paint(g.screen, dragOffset(dx, window.innerWidth), window.innerWidth);
}

function onTouchEnd(e: TouchEvent): void {
  const g = gesture;
  if (!g) return;
  const t = findTouch(e, g.id);
  if (!t) return;
  if (g.phase !== 'tracking') {
    endGesture();
    return;
  }
  const dx = t.clientX - g.x0;
  const dt = Math.max(1, performance.now() - g.prevT);
  const velocity = (t.clientX - g.prevX) / dt;
  if (shouldCommit(dx, window.innerWidth, velocity)) commit(window.innerWidth);
  else springBack();
}

function onTouchCancel(): void {
  if (!gesture) return;
  springBack();
}

function detachMoveListeners(): void {
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend', onTouchEnd);
  window.removeEventListener('touchcancel', onTouchCancel);
}

function onTouchStart(e: TouchEvent): void {
  if (gesture) {
    // A second finger during a swipe abandons it (pinch/zoom, two-thumb play).
    if (e.touches.length > 1) endGesture();
    return;
  }
  if (!enabled || modalOpen) return;
  if (e.touches.length !== 1) return;
  const t = e.touches[0];
  if (!isEdgeStart(t.clientX)) return;
  if (isTyping()) return;

  const active = pickActive(entries.map((entry) => ({ entry, visible: isElementVisible(entry.el) })));
  if (!active) return;
  const screen = active.entry.el.closest('.screen');
  if (!(screen instanceof HTMLElement)) return;
  if (blocksSwipe(e.target, screen)) return;

  clearSettle();
  resetScreen(screen);
  const now = performance.now();
  gesture = {
    id: t.identifier,
    x0: t.clientX,
    y0: t.clientY,
    phase: 'pending',
    entry: active.entry,
    screen,
    lastX: t.clientX,
    lastT: now,
    prevX: t.clientX,
    prevT: now,
  };
  // Steady state stays fully passive — the non-passive move listener only
  // exists while a candidate gesture is alive.
  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('touchend', onTouchEnd);
  window.addEventListener('touchcancel', onTouchCancel);
}

let installed = false;

/** Install the edge-swipe listener (once, from Flow). Touch only by design —
 *  a mouse has a back button and a visible chevron. */
export function installSwipeBack(host: EventTarget = document): void {
  if (installed) return;
  installed = true;
  host.addEventListener('touchstart', onTouchStart as EventListener, { passive: true });
}
